import { useState, useCallback } from 'react';
import { logger } from '@utils/logger';

interface UseClipboardReturn {
  pasteFromClipboard: () => Promise<string | null>;
  copyToClipboard: (text: string) => Promise<boolean>;
  error: string;
  clearError: () => void;
}

/**
 * Universal clipboard hook that works across Chrome, Edge, Firefox, and Safari
 * 
 * Key considerations:
 * - Safari: Requires clipboard API to be called immediately in event handler (no await before)
 * - Firefox: May require explicit permission, provides helpful error messages
 * - Chrome/Edge: Works well with modern Clipboard API
 * 
 * Provides graceful fallbacks and user-friendly error messages
 */
export const useClipboard = (): UseClipboardReturn => {
  const [error, setError] = useState('');

  const clearError = useCallback(() => setError(''), []);

  /**
   * Universal paste function that works across all browsers
   * Chrome/Edge/Firefox/Safari compatible
   */
  const pasteFromClipboard = useCallback(async (): Promise<string | null> => {
    setError('');

    // Check if Clipboard API is available
    if (!navigator.clipboard || !navigator.clipboard.readText) {
      setError('⚠️ Clipboard API not supported in this browser. Please paste manually using Ctrl+V (Cmd+V on Mac)');
      return null;
    }

    try {
      // CRITICAL for Safari: Call clipboard API immediately, no await before this
      // This must be the first async operation in the event handler
      const text = await navigator.clipboard.readText();
      return text;
    } catch (clipboardError) {
      logger.error('Clipboard API failed', clipboardError);
      
      // Provide browser-specific helpful error messages
      const errorMessage = clipboardError instanceof Error ? clipboardError.message.toLowerCase() : '';
      const userAgent = navigator.userAgent.toLowerCase();
      const isSafari = userAgent.includes('safari') && !userAgent.includes('chrome');
      const isFirefox = userAgent.includes('firefox');
      
      if (errorMessage.includes('permission') || errorMessage.includes('denied') || errorMessage.includes('not allowed')) {
        if (isSafari) {
          setError('🍎 Safari requires clipboard permission. Please allow clipboard access when prompted, or paste manually using Cmd+V');
        } else if (isFirefox) {
          setError('🦊 Firefox requires clipboard permission. Click the permission icon in the address bar to allow, or paste manually using Ctrl+V');
        } else {
          setError('📋 Clipboard access denied. Please grant permission or paste manually using Ctrl+V (Cmd+V on Mac)');
        }
      } else {
        setError('⚠️ Could not access clipboard. Please paste manually using Ctrl+V (Windows/Linux) or Cmd+V (Mac)');
      }
      
      return null;
    }
  }, []);

  /**
   * Universal copy function that works across all browsers
   * Includes fallback for older browsers
   */
  const copyToClipboard = useCallback(async (text: string): Promise<boolean> => {
    setError('');

    // Method 1: Try modern Clipboard API
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        // CRITICAL for Safari: Call clipboard API immediately
        await navigator.clipboard.writeText(text);
        return true;
      } catch (clipboardError) {
        logger.error('Clipboard write failed, trying fallback', clipboardError);
        // Fall through to fallback method
      }
    }

    // Method 2: Fallback using deprecated but widely supported execCommand
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      textarea.style.left = '-999999px';
      document.body.appendChild(textarea);
      textarea.select();
      
      const success = document.execCommand('copy');
      document.body.removeChild(textarea);
      
      if (success) {
        return true;
      }
    } catch (fallbackError) {
      logger.error('Fallback copy failed', fallbackError);
    }

    setError('❌ Failed to copy to clipboard');
    return false;
  }, []);

  return {
    pasteFromClipboard,
    copyToClipboard,
    error,
    clearError,
  };
};

